const SCREENSHOT_SCALE = 2

const decodeHtml = (html) => {
  const element = document.createElement('div')
  element.innerHTML = html || ''
  return element.textContent || element.innerText || ''
}

const getFileName = (name, type) =>
  `${type}-${decodeHtml(name)
    .toLowerCase()
    .replace(/[^a-zа-яё0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '') || 'marvel-snap'}.png`

const createDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const getAbsoluteCssUrl = (rawUrl, sheetHref) => {
  if (
    !rawUrl ||
    rawUrl.startsWith('data:') ||
    rawUrl.startsWith('http://') ||
    rawUrl.startsWith('https://') ||
    rawUrl.startsWith('#')
  ) {
    return rawUrl
  }

  const baseUrl = sheetHref || window.location.href
  return new URL(rawUrl, baseUrl).href
}

const inlineCssUrls = async (cssText, sheetHref) => {
  const matches = Array.from(cssText.matchAll(/url\((['"]?)(.*?)\1\)/g))
  let result = cssText

  for (const match of matches) {
    const [fullMatch, , rawUrl] = match
    if (
      !rawUrl ||
      rawUrl.startsWith('#')
    ) {
      continue
    }

    const absoluteUrl = getAbsoluteCssUrl(rawUrl, sheetHref)

    try {
      const dataUrl = absoluteUrl.startsWith('data:')
        ? absoluteUrl
        : await imageToDataUrl(absoluteUrl)
      result = result.split(fullMatch).join(`url("${dataUrl}")`)
    } catch (error) {
      result = result.split(fullMatch).join('none')
    }
  }

  return result
}

const getElementClassNames = (root) => {
  const classNames = new Set()
  root.querySelectorAll('*').forEach((node) => {
    node.classList?.forEach((className) => classNames.add(className))
  })
  root.classList?.forEach((className) => classNames.add(className))
  return Array.from(classNames)
}

const ruleMatchesElement = (ruleText, classNames) => {
  if (ruleText.startsWith('@font-face')) return true
  return classNames.some((className) => ruleText.includes(`.${className}`))
}

const getDocumentStyles = async (root) => {
  const styles = []
  const classNames = getElementClassNames(root)

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = Array.from(sheet.cssRules || [])
      for (const rule of rules) {
        if (ruleMatchesElement(rule.cssText, classNames)) {
          styles.push(await inlineCssUrls(rule.cssText, sheet.href))
        }
      }
    } catch (error) {
      // Cross-origin styles are ignored for screenshot rendering.
    }
  }

  styles.push(`
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    .screenshot-root {
      width: ${root.style.width || 'auto'};
      min-height: ${root.style.height || 'auto'};
      background: transparent;
      color: #ffffff;
      font-family: Roboto, Arial, sans-serif;
      line-height: 1.6;
    }

    .screenshot-root img {
      vertical-align: top;
    }

    .screenshot-root .card,
    .screenshot-root .location {
      color: #ffffff;
      font-family: Roboto, Arial, sans-serif;
    }

    .screenshot-root .card-name,
    .screenshot-root .location-name {
      color: #ffffff;
      font-weight: 900;
      font-size: 1.1rem;
      margin: 0;
    }

    .screenshot-root .card-descr,
    .screenshot-root .location-descr {
      color: rgba(255, 255, 255, 0.8);
      font-size: 1rem;
      margin: 0;
    }

    .screenshot-root .card-descr b,
    .screenshot-root .location-descr b {
      color: #ffffff;
    }

    .item-share-button {
      display: none !important;
    }
  `)

  return styles.join('\n')
}

const imageToDataUrl = async (src) => {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 5000)
  let response
  try {
    response = await fetch(src, { signal: controller.signal })
  } finally {
    clearTimeout(timeout)
  }
  const blob = await response.blob()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const inlineImages = async (root) => {
  const images = Array.from(root.querySelectorAll('img'))

  await Promise.all(
    images.map(async (image) => {
      const src = image.currentSrc || image.src
      if (!src || src.startsWith('data:')) return

      try {
        image.setAttribute('src', await imageToDataUrl(src))
      } catch (error) {
        image.setAttribute('src', src)
      }
    })
  )
}

const waitForImages = async (root) => {
  const images = Array.from(root.querySelectorAll('img'))

  await Promise.all(
    images.map(
      (image) =>
        image.complete ||
        new Promise((resolve) => {
          image.onload = resolve
          image.onerror = resolve
        })
    )
  )
}

const getContentHeight = (element) => {
  const rect = element.getBoundingClientRect()
  const styles = window.getComputedStyle(element)
  const paddingBottom = Number.parseFloat(styles.paddingBottom) || 0
  const children = Array.from(element.children).filter((child) => {
    const childStyles = window.getComputedStyle(child)
    return childStyles.position !== 'absolute' && childStyles.display !== 'none'
  })
  const contentBottom = children.reduce((bottom, child) => {
    const childRect = child.getBoundingClientRect()
    return Math.max(bottom, childRect.bottom - rect.top)
  }, 0)

  return Math.ceil(contentBottom + paddingBottom)
}

const canvasToBlob = (canvas) =>
  new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png')
  })

const renderSvgToCanvas = (svg, width, height) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
    const timeout = setTimeout(() => {
      reject(new Error('Screenshot rendering timed out.'))
    }, 10000)

    image.onload = () => {
      clearTimeout(timeout)
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(width * SCREENSHOT_SCALE)
      canvas.height = Math.ceil(height * SCREENSHOT_SCALE)

      const ctx = canvas.getContext('2d')
      ctx.scale(SCREENSHOT_SCALE, SCREENSHOT_SCALE)
      ctx.drawImage(image, 0, 0, width, height)

      resolve(canvas)
    }

    image.onerror = (error) => {
      clearTimeout(timeout)
      reject(error)
    }

    image.src = url
  })

const getColorDistance = (data, index, background) =>
  Math.abs(data[index] - background[0]) +
  Math.abs(data[index + 1] - background[1]) +
  Math.abs(data[index + 2] - background[2]) +
  Math.abs(data[index + 3] - background[3])

const clipRoundedRect = (ctx, width, height, radius) => {
  const r = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(r, 0)
  ctx.arcTo(width, 0, width, height, r)
  ctx.arcTo(width, height, 0, height, r)
  ctx.arcTo(0, height, 0, 0, r)
  ctx.arcTo(0, 0, width, 0, r)
  ctx.closePath()
  ctx.clip()
}

const cropCanvasBottomToContent = (canvas, padding) => {
  const ctx = canvas.getContext('2d')
  const { width, height } = canvas
  const imageData = ctx.getImageData(0, 0, width, height)
  const { data } = imageData
  const scanInset = Math.ceil(padding * 1.5)
  const sampleX = Math.floor(width / 2)
  const sampleY = Math.max(0, height - Math.ceil(padding / 2) - 1)
  const sampleIndex = (sampleY * width + sampleX) * 4
  const background = [
    data[sampleIndex],
    data[sampleIndex + 1],
    data[sampleIndex + 2],
    data[sampleIndex + 3],
  ]
  let contentBottom = height - 1

  for (let y = height - 1; y >= 0; y -= 1) {
    let hasContent = false

    for (let x = scanInset; x < width - scanInset; x += 4) {
      const index = (y * width + x) * 4
      if (getColorDistance(data, index, background) > 42) {
        hasContent = true
        break
      }
    }

    if (hasContent) {
      contentBottom = y
      break
    }
  }

  const croppedHeight = Math.min(height, contentBottom + Math.ceil(padding) + 1)
  if (croppedHeight >= height) return canvas

  const cropped = document.createElement('canvas')
  cropped.width = width
  cropped.height = croppedHeight
  const croppedCtx = cropped.getContext('2d')
  croppedCtx.save()
  clipRoundedRect(croppedCtx, width, croppedHeight, 12 * SCREENSHOT_SCALE)
  croppedCtx.drawImage(canvas, 0, 0)
  croppedCtx.restore()
  return cropped
}

export const downloadElementScreenshot = async ({ element, filenameBase, type }) => {
  if (!element) return

  if (document.fonts?.ready) {
    await document.fonts.ready
  }

  await waitForImages(element)

  const rect = element.getBoundingClientRect()
  const elementStyles = window.getComputedStyle(element)
  const paddingTop = Number.parseFloat(elementStyles.paddingTop) || 0
  const width = Math.ceil(rect.width)
  const height = Math.min(Math.ceil(rect.height), getContentHeight(element))
  const clone = element.cloneNode(true)

  clone.style.width = `${width}px`
  clone.style.height = `${height}px`
  clone.style.margin = '0'
  clone.style.boxSizing = 'border-box'

  await inlineImages(clone)

  const html = new XMLSerializer().serializeToString(clone)
  const styles = await getDocumentStyles(clone)
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <foreignObject width="100%" height="100%">
        <div class="screenshot-root" xmlns="http://www.w3.org/1999/xhtml">
          <style><![CDATA[${styles}]]></style>
          ${html}
        </div>
      </foreignObject>
    </svg>
  `

  const canvas = cropCanvasBottomToContent(
    await renderSvgToCanvas(svg, width, height),
    paddingTop * SCREENSHOT_SCALE
  )
  const blob = await canvasToBlob(canvas)

  if (blob) {
    createDownload(blob, getFileName(filenameBase, type))
  }
}
